import React, { useState } from 'react';
import { ColumnType, TablePaginationConfig } from 'antd/lib/table';

import './index.less';

import { ELMA365ToolAvailability, ELMA365ToolModel } from '../../../../../../gamma-api';
import IbIcon from '../../../../../components/common/IbIcon';
import IbButton from '../../../../../components/common/IbButton';
import IbTable from '../../../../../components/common/IbTable';
import IbContextMenu, { IIbContextMenuItem } from '../../../../../components/common/IbContextMenu';
import IbPagination from '../../../../../components/common/IbPagination';
import { TOOLS_PAGE_SIZE } from '../const';
import IbTypography from '../../../../../components/common/IbTypography';
import IbTextHighlighter from '../../../../../components/common/IbTextHighlighter';
import { ELMA365_TOOL_ACTION_ICON_NAMES, ELMA365_COMPONENT_TYPE_ICON_NAMES } from '../../const';
import IbSwitch from '../../../../../components/common/IbSwitch';
import { getTranslateFunc } from '../../../../../utils/stringUtil';

import {
  MAIN_CLASS_NAME,
  CELL_CLASS_NAME,
  PAGINATION_ROW_CLASS_NAME,
  COMPONENT_CELL_CLASS_NAME,
  TABLE_ROW_CLASS_NAME,
  TABLE_ROW_DISABLED_CLASS_NAME,
} from './const';

export interface IToolTableProps {
  dataLoading: boolean;
  pageLoading: boolean;
  tools: ELMA365ToolModel[];
  pagination: TablePaginationConfig;
  searchText?: string;
  onOpen?: (tool: ELMA365ToolModel) => void;
  onDelete?: (tool: ELMA365ToolModel) => void;
  onPaginationChange?: (page: number) => void;
  onToolAvailabilityChange: (tool: ELMA365ToolModel, availability: ELMA365ToolAvailability) => Promise<void>;
}

const ToolTable: React.FC<IToolTableProps> = ({
  dataLoading,
  pageLoading,
  tools,
  pagination,
  searchText,
  onOpen,
  onDelete,
  onPaginationChange,
  onToolAvailabilityChange,
}) => {
  const translate = getTranslateFunc('Toolkits: ELMA365: Toolset: Tool table:');

  const [selectedTool, setSelectedTool] = useState<ELMA365ToolModel>();
  const [switchTool, setSwitchTool] = useState<ELMA365ToolModel>();
  const [saving, setSaving] = useState(false);

  const onMenuClose = () => {
    setSelectedTool(undefined);
  };

  const toggleAvailability = async (tool: ELMA365ToolModel) => {
    setSaving(true);
    setSwitchTool(tool);

    await onToolAvailabilityChange(
      tool,
      tool.availability === ELMA365ToolAvailability.Enabled
        ? ELMA365ToolAvailability.Disabled
        : ELMA365ToolAvailability.Enabled
    );

    setSaving(false);
    setSwitchTool(undefined);
  };

  const renderAvailabilitySwitch = (tool: ELMA365ToolModel) => {
    const enabled = tool.availability === ELMA365ToolAvailability.Enabled;
    const processing = saving && switchTool?.tool_id === tool.tool_id;

    const onSwitchChange = async (_: boolean, event: MouseEvent) => {
      event.stopPropagation();
      await toggleAvailability(tool);
    };

    const onCellSwitchClick = async (event: React.MouseEvent) => {
      event.stopPropagation();
      await toggleAvailability(tool);
    };

    return (
      <IbTypography>
        <IbTypography.Paragraph disabled={!enabled} type="secondary">
          <div className={CELL_CLASS_NAME} onClick={onCellSwitchClick}>
            <IbSwitch checked={enabled} loading={processing} size="small" onChange={onSwitchChange} />
            <span>{enabled ? translate('Enabled') : translate('Disabled')}</span>
          </div>
        </IbTypography.Paragraph>
      </IbTypography>
    );
  };

  const renderNamespace = (tool: ELMA365ToolModel) => {
    return (
      <IbTypography.Paragraph type="secondary">
        <IbTextHighlighter highlightedText={searchText} text={tool.namespace.identity.name} />
      </IbTypography.Paragraph>
    );
  };

  const renderComponent = (tool: ELMA365ToolModel) => {
    return (
      <span className={`${CELL_CLASS_NAME} ${COMPONENT_CELL_CLASS_NAME}`}>
        <IbIcon iconName={ELMA365_COMPONENT_TYPE_ICON_NAMES[tool.component.identity.type]} size={20} />
        <IbTypography.Paragraph type="secondary">
          <IbTextHighlighter highlightedText={searchText} text={tool.component.identity.name} />
        </IbTypography.Paragraph>
      </span>
    );
  };

  const renderName = (tool: ELMA365ToolModel) => {
    return (
      <span className={CELL_CLASS_NAME}>
        <IbIcon iconName={ELMA365_TOOL_ACTION_ICON_NAMES[tool.action]} size={20} />
        <IbTypography.Paragraph ellipsis type="secondary">
          <IbTextHighlighter highlightedText={searchText} text={tool.attributes.name.value} />
        </IbTypography.Paragraph>
      </span>
    );
  };

  const renderDescription = (tool: ELMA365ToolModel) => {
    return (
      <IbTypography.Paragraph type="secondary">
        <IbTextHighlighter highlightedText={searchText} text={tool.attributes.title.value} />
      </IbTypography.Paragraph>
    );
  };

  const renderContext = (tool: ELMA365ToolModel) => {
    const onMoreButtonClick = () => {
      setSelectedTool(tool);
    };

    const onOpenSelect = () => {
      onOpen?.(tool);
    };

    const onDeleteSelect = () => {
      onDelete?.(tool);
    };

    const menuItems: IIbContextMenuItem[] = [
      {
        icon: <IbIcon iconName="view-list" size={20} />,
        text: translate('Open'),
        onSelect: onOpenSelect,
      },
      {
        icon: <IbIcon iconName="delete" size={20} />,
        text: translate('Delete'),
        onSelect: onDeleteSelect,
      },
    ];

    return (
      <IbContextMenu menuItems={menuItems} visible={tool.tool_id == selectedTool?.tool_id} onClose={onMenuClose}>
        <IbButton icon={<IbIcon iconName="more-one" />} type="icon" onClick={onMoreButtonClick} />
      </IbContextMenu>
    );
  };

  const columns: ColumnType<ELMA365ToolModel>[] = [
    {
      title: translate('Namespace'),
      render: renderNamespace,
      ellipsis: false,
      width: '16%',
    },
    {
      title: translate('Component'),
      render: renderComponent,
      ellipsis: false,
      width: '20%',
    },
    {
      title: translate('Name'),
      render: renderName,
      ellipsis: true,
      width: '25%',
    },
    {
      title: translate('Description'),
      render: renderDescription,
      ellipsis: false,
    },
    {
      title: translate('Availability'),
      width: '12%',
      render: (_, tool) => renderAvailabilitySwitch(tool),
    },
    {
      title: '',
      render: renderContext,
      width: 40,
    },
  ];

  const emptyText = translate(searchText ? 'Empty content: No tools found' : 'Empty content: No tools at all');

  const onRowClick = (tool: ELMA365ToolModel, column: ColumnType<unknown>) => {
    if (column.title) {
      onOpen?.(tool);
    }
  };

  const rowClassNamesFunc = (tool: ELMA365ToolModel) => {
    const rowClassNames = [TABLE_ROW_CLASS_NAME];
    if (tool.availability === ELMA365ToolAvailability.Disabled) {
      rowClassNames.push(TABLE_ROW_DISABLED_CLASS_NAME);
    }

    return rowClassNames;
  };

  return (
    <div className={MAIN_CLASS_NAME}>
      <div className={TABLE_ROW_CLASS_NAME}>
        <IbTable
          className={MAIN_CLASS_NAME}
          columns={columns}
          dataSource={tools}
          emptyText={emptyText}
          loading={dataLoading || pageLoading}
          rowClassNamesFunc={rowClassNamesFunc}
          rowKey="tool_id"
          onRowClick={onRowClick}
        />
      </div>
      {!dataLoading && (pagination.total ?? 0) > TOOLS_PAGE_SIZE && (
        <div className={PAGINATION_ROW_CLASS_NAME}>
          <IbPagination
            current={pagination.current}
            defaultCurrent={1}
            defaultPageSize={TOOLS_PAGE_SIZE}
            pageSize={TOOLS_PAGE_SIZE}
            showSizeChanger={false}
            total={pagination.total}
            onChange={onPaginationChange}
          />
        </div>
      )}
    </div>
  );
};

export default ToolTable;
